import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import {
    pathOr, map, propEq, find, T, always, cond,
    equals, filter, includes
} from 'ramda';
import React, { useState, useContext } from 'react';
import { renderHandlebars } from 'src/utilities/handlebars';
import { getFooterLinks } from 'src/utilities/logo';
import Modal from 'src/components/Modal/TermsModal';
import LinkButton from "src/components/Buttons/LinkButton";
import { PermissionContext } from 'src/features/RBAC/PermissionContext';
import { isNotEmpty } from 'src/utilities/hasValue';
import {
    CONSENT_E_STATEMENTS,
    CONSENT_DIGITAL
} from 'src/constants';
import { useMediaQuery } from '@mui/material';
import siteSetting from 'src/siteSetting';

interface Props {
    loanType?: string;
}

const CONTENT_HBS = 'hbs';
const CONTENT_EXTERNAL_URL = 'external_url';
const CONTENT_OPEN_EXTERNAL_URL = 'open_external_url';

type CombinedProps = Props;
const ProductFooter: React.FC<CombinedProps> = (props: CombinedProps) => {
    const { loanType } = props;
    const [open, setOpen] = useState(false)
    const [activeModalTitle, setActiveModalModalTitle] = useState('');
    const [currentTermsText, setCurrentTermsText] = useState('');
    const [currentTermsPDFLink, setCurrentTermsPDFLink] = useState('');
    const [externalURL, setExternalURL] = useState('');
    const [openIframe, setOpenIframe] = useState(false);

    const { palette } = useTheme();
    const { loanProducts } = useContext(PermissionContext);
    const { defaulLinks = [] } = getFooterLinks(loanProducts, loanType, '', 'default');
    const excludeList = [CONSENT_DIGITAL, CONSENT_E_STATEMENTS];
    const filteredLinks = filterLinks(defaulLinks, excludeList);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));

    const openExternalUrl = (url: string) => {
      // We certainly could manage the link using an href in the tsx element.
      // However, for consistency I am managing the external url here.
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    }


    function getContent(policyOptions: any) {
        const currentTitle = pathOr('', ['name'], policyOptions);
        setCurrentTermsText('');

        let contentType;
        if (policyOptions.iframe_external_url) {
          contentType = CONTENT_EXTERNAL_URL;
        } else if (policyOptions.open_external_url) {
          contentType = CONTENT_OPEN_EXTERNAL_URL;
        } else {
          contentType = CONTENT_HBS;
        }

        const contentLoaderFn = cond([
            [equals(CONTENT_HBS), always(() => loadHBSContent(currentTitle))],
            [equals(CONTENT_EXTERNAL_URL), always(() => loadExternalURL(policyOptions))],
            [equals(CONTENT_OPEN_EXTERNAL_URL), always(() => openExternalUrl(policyOptions.open_external_url || ''))],
            [T, always(() => loadHBSContent(currentTitle))]
        ])(contentType);
        contentLoaderFn();
    }



    const handleOpen = (policyOptions: any) => {
        getContent(policyOptions)
    }

    const loadHBSContent = (policyName: string) => {
        const currentLink = find(propEq('name', policyName), filteredLinks);
        setCurrentTermsPDFLink(pathOr('', ['public_pdf_url'], currentLink));
        fetch(pathOr('', ['public_hbs_url'], currentLink))
            .then((res) => res.text())
            .then((text) => {
                const data = pathOr({}, ['data'], currentLink)
                setCurrentTermsText(renderHandlebars(text, data));
            });
        setOpen(true)
        setActiveModalModalTitle(policyName);
    }

    const handleDownloadPDF = () => {
        // using Java Script method to get PDF file
        fetch(currentTermsPDFLink).then((response) => {
            response.blob().then((blob) => {
                // Creating new object of PDF file
                const fileURL = window.URL.createObjectURL(blob);
                // Setting various property values
                let alink = document.createElement("a");
                alink.href = fileURL;
                alink.download = `${activeModalTitle}.pdf`;
                alink.click();
            });
        });
    }

    const handleClose = () => setOpen(false);
    const handleIframeClose = () => setOpenIframe(false);

    const loadExternalURL = (policyOptions: any) => {
        setActiveModalModalTitle(pathOr('', ['name'], policyOptions));
        setExternalURL(pathOr('', ['iframe_external_url'], policyOptions))
        setOpenIframe(true)
    }

    const renderIframModal = () => {
        return <Modal
            title={activeModalTitle}
            handleClose={handleIframeClose}
            isOpen={openIframe}
            width="100%"
        ><Box >
                <iframe width="100%" height={"500px"} src={externalURL} />
            </Box>
        </Modal>
    }

    /**
     * Filters out links that have specific property names.
     *
     * @param {any[]} defaulLinks - The array of link objects to be filtered.
     * @param {string[]} excludeList - The list of property names to be excluded.
     * @returns {any[]} - The filtered array of link objects.
     *
     * This function filters out link objects from the input array where the `name` property
     * matches any of the names in the excludeList.
     *
     * Example usage:
     * const defaulLinks = [
     *     { name: 'LINK_1' },
     *     { name: 'CONSENT_DIGITAL' },
     *     { name: 'LINK_2' },
     *     { name: 'CONSENT_E_STATEMENTS' }
     * ];
     * const excludeList = ['CONSENT_DIGITAL', 'CONSENT_E_STATEMENTS'];
     * const filteredLinks = filterLinks(defaulLinks, excludeList);
     * console.log(filteredLinks); // Output: [{ name: 'LINK_1' }, { name: 'LINK_2' }]
     */
    function filterLinks(defaultLinks: any[], excludeList: string[]) {
        return filter(
            (item: any) => {
                const propertyName: string = pathOr('', ['name'], item);
                return !includes(propertyName, excludeList);
            },
            defaultLinks
        );
    }

    return (
        <>
            <Box sx={{
                bottom: 0,
                width: '100%',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: matches? "column": "row",
                alignItems: matches? "flex-start":'center',
            }}>
                {map((item: any) => <LinkButton
                    text={pathOr('', ['name'], item)}
                    sx={{
                        padding: 0,
                        fontSize: 12,
                        color: siteSetting.primaryDarkColor(),
                        marginLeft: '3px',
                        fontWeight: 400,
                        fontFamily: 'Open Sans',
                        alignItems: 'baseline',
                        '&:hover': {
                            color: pathOr('', ['primaryColor'], palette),
                            backgroundColor: 'unset',
                        }
                    }}
                    onClick={() => handleOpen(item)}
                />, filteredLinks)}
            </Box>
            <Modal
                title={activeModalTitle}
                handleClose={handleClose}
                isOpen={open}
                isDownloadPDFLink={isNotEmpty(currentTermsPDFLink)}
                handleDownloadPDF={handleDownloadPDF}
            >
                <Box
                    sx={{
                        mt: 2,
                        minWidth: '100%',
                        wordWrap: 'break-word', // Ensures text wraps to the next line if the container is too small
                        overflowWrap: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{ __html: currentTermsText }}
                />
            </Modal>
            {openIframe && renderIframModal()}
        </>
    );
};

export default ProductFooter;
